import {cloneDeep, last} from 'lodash';
import {ulid} from 'ulid';
import {BusRoute} from './busRoute';
import {ShiftBat} from './shiftBat';
import dayjs from '../dayjs';
import {getDistanceInMetres, normaliseDate} from '../libs/routes-lib';
import {DATE_STRING, PUBLIC_HOLIDAY_PERIODS} from './schedule';
import {defaultRates} from '../libs/eaPayments';
import {DEFAULT_VEHICLE_RATE} from './vehicleType';
import config from '../config';
import { formatToDateString } from '../libs/date';

export const BOOKING_STATUS = {
    new: {
        label: "New Booking",
        value: "new",
        color: "default",
    },
    accepted: {
        label: "Booking Confirmed",
        value: "accepted",
        color: "success",
    },
    scheduled: {
        label: "Scheduled",
        value: "scheduled",
        color: "warning",
    },
    allocated: {
        label: "Allocated",
        value: "allocated",
        color: "success",
    },
    completed: {
        label: "Completed",
        value: "completed",
        color: "success",
    },
    cancelled: {
        label: "Cancelled",
        value: "cancelled",
        color: "error",
    },
}

export const CHARTER_STATUS = Object.values(BOOKING_STATUS);

export const QUOTE_STATUS = {
    draft: {
        label: "Draft",
        value: "draft",
        color: "default",
    },
    sent: {
        label: "Sent",
        value: "sent",
        color: "default",
    },
    accepted: {
        label: "Accepted",
        value: "accepted",
        color: "success",
    },
    invoiceDraft: {
        label: "Draft",
        value: "invoice-draft",
        color: "default",
    },
    awaitingPayment: {
        label: "Awaiting Payment",
        value: "awaiting-payment",
        color: "warning",
    },
    paid: {
        label: "Paid",
        value: "paid",
        color: "success",
    },
}

export class Booking {
    constructor(props) {
        this.bookingId = ulid();
        this.status = 'new';
        this.charters = [];
        Object.assign(this, props);

        if (this.charters?.length) {
            this.charters = this.charters.map(charter => new Charter(charter));
        }
    }
}

export class Charter {
    constructor(props) {
        this.charterId = '_';
        this.bookingReference = null;
        this.status = BOOKING_STATUS.new.value;
        this.type = 'charter#detail';
        this.payType = 'Rate of the day';
        this.charterType = 'standard';
        this.passengerCount = null;
        this.itineraryId = '_';
        this.quoteLineItems = [];
        this.itinerary = []; // Routes to be used in the duty
        this.duty = null; // ShiftBat
        this.quote = null;
        this.duties = []; // ShiftBats for this charter
        this.destinations = [];
        this.startDate = dayjs();
        this.dates = [];
        this.manualAdjustments = {
            unitPrice: 0,
            quantity: 0
        }; // Track manual adjustments to auto-generated quote
        this.discount = null; // Charter-specific discount percentage (overrides customer default)
        this.marginInclusion = null; // Charter-specific margin inclusion percentage (overrides operator default)

        // Document state and timestamps
        this.quoteStatus = QUOTE_STATUS.draft.value;
        this.quoteHistory = [];
        this.quoteDate = null;
        this.confirmationDate = null;
        this.invoiceDate = null;
        this.receiptDate = null;

        // Initialize as null - their presence indicates the state
        this.confirmationDetails = null;
        this.invoiceDetails = null;
        this.receiptDetails = null;

        // const arvo = dayjs().hour() > 12;
        // this.startDate = arvo ? dayjs().add(1, 'd') : dayjs();
        // this.startTime = arvo ? 8 * 3600 : 12 * 3600;

        this.startTime = null;
        this.endDate = null;
        this.endTime = null;
        this.customer = null;
        this.customerId = null;
        this.name = null;
        this.description = null;
        this.express = {
            startDepot: null,
            endDepot: null
        };
        this.vehicleTypes = [];
        this.autoGeneratedQuote = false;
        this.duration = 0;
        this.distance = 0;
        this.scheduleId = null;
        
        Object.assign(this, props);
        
        if (this.itinerary?.length) {
            this.itinerary = this.itinerary.map(i => typeof i === 'object' ? new BusRoute(i) : i);
        }
        if (this.duty && !this.duties?.length) {
            this.duties = [this.duty];
            delete this.duty;
        }
        if (this.duties) {
            this.duties = this.duties.map(duty => typeof duty === 'object' ? new ShiftBat(duty) : duty);
        }
        if (typeof this.startDate === 'string') {
            this.startDate = dayjs(this.startDate, DATE_STRING).startOf('day');
        }
        if (this.dates?.length) {
            this.dates = this.dates.map(date => {
                return normaliseDate(date)?.startOf('day') || null;
            });
        }
        if (typeof this.endDate === 'string') {
            this.endDate = dayjs(this.endDate, DATE_STRING).startOf('day');
        }
        if (typeof this.quoteDate === 'string') {
            this.quoteDate = dayjs(this.quoteDate, DATE_STRING);
        }
        if (typeof this.paymentDue === 'string') {
            this.paymentDue = dayjs(this.paymentDue, DATE_STRING);
        }
        
    }

    /**
     * Return the number of hours required for each trip in the charter's itinerary for each day of the charter using the itinerary's trips' stop times
     */
    getDriverRequirements() {
        const driverRequirements = {};
        this.itinerary.forEach(itinerary => {
            driverRequirements[itinerary.routeId] = new BusRoute(itinerary).getDriverRequirements();
        });
        return driverRequirements;
    }

    getRunningDates(schedulesById, maxDays = 20) {
        let dates;
        if(this.charterType === 'standard') {
            dates = this.dates ? [...this.dates] : [];
        } else if(this.charterType === 'recurring') {
            const schedule = schedulesById[this.scheduleId];
            if (schedule) {
                dates = schedule.getRunningDates(dayjs(), maxDays);
            }
        }
        if(!dates) {
            dates = [dayjs()];
        }
        return dates;
    }

    generateBookingReference(existingBookings = []) {
        const existingBookingReferencesSet = new Set(existingBookings.map(b => b.bookingReference));
        // Generate a 6-character reference using letters and numbers
        const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const allChars = letters + numbers;
        let reference = '';
        
        // Ensure at least one letter and one number
        reference += letters.charAt(Math.floor(Math.random() * letters.length));
        reference += numbers.charAt(Math.floor(Math.random() * numbers.length));
        
        // Fill the rest with random characters
        for (let i = 0; i < 4; i++) {
            reference += allChars.charAt(Math.floor(Math.random() * allChars.length));
        }
        
        // Shuffle the reference to mix letters and numbers
        reference = reference.split('').sort(() => Math.random() - 0.5).join('');
        if (existingBookingReferencesSet.has(reference)) {
            return this.generateBookingReference(existingBookings);
        }
        return reference;
    }

    calculateQuoteForDuty(duty) {
        if (!duty) {
            return 0;
        }
        const vehicleCost = Math.ceil(duty?.vehicleRate * duty.distance);
        const driverCost = Math.ceil(duty?.driverRate * duty.duration);
        return Math.ceil((vehicleCost + driverCost) * duty.days * (1 + duty.loading / 100));
    }

    calculateQuote() {
        if (!this.quote) {
            return 0;
        }
        return Object.keys(this.quote).reduce((acc, key) => acc + this.calculateQuoteForDuty(this.quote[key]), 0);
    }

    /**
     * Calculate distance between two points using Geoapify API
     * @param {number} lat1 - Latitude of first point
     * @param {number} lng1 - Longitude of first point
     * @param {number} lat2 - Latitude of second point
     * @param {number} lng2 - Longitude of second point
     * @returns {Promise<number>} Distance in meters
     */
    async calculateGeoapifyDistance(lat1, lng1, lat2, lng2) {
        try {
            const response = await fetch(`https://api.geoapify.com/v1/routing?waypoints=${lat1},${lng1}|${lat2},${lng2}&mode=drive&apiKey=${config.maps.geoapify.ctrxs}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });
            
            const data = await response.json();
            if (data.features && data.features.length > 0) {
                // Return distance in meters
                return data.features[0].properties.legs[0].distance;
            }
            
            // Return 0 if API call fails
            return 0;
        } catch (error) {
            console.error('Error calculating distance with Geoapify:', error);
            // Return 0 if API call fails
            return 0;
        }
    }

    /**
     * Check if a date is a public holiday
     * @param {Date|string} date - The date to check
     * @returns {boolean} True if the date is a public holiday
     */
    isHoliday(date) {
        const checkDate = dayjs(date);
        const year = checkDate.year();
        
        // Check if the year exists in the holiday periods
        if (!PUBLIC_HOLIDAY_PERIODS[year]) {
            return false;
        }
        
        // Check if the date falls within any of the holiday periods
        return PUBLIC_HOLIDAY_PERIODS[year].some(period => 
            checkDate.isBetween(period.start, period.end, null, '[]')
        );
    }

    /**
     * Get the effective discount percentage for this charter
     * @returns {number} - The effective discount percentage (0-100)
     */
    getDiscount() {
        // If charter has a specific discount, use it
        if (this.discount !== null && this.discount !== undefined) {
            return this.discount;
        }

        // If a customer is attached and has a discount, use that
        if (this.customer && this.customer.getDiscount) {
            return this.customer.getDiscount();
        }

        // Otherwise fall back to operator settings default
        return 0;
    }

    getMarginInclusion(operator) {
        // If charter has a specific margin inclusion, use it
        if (this.marginInclusion !== null && this.marginInclusion !== undefined) {
            return this.marginInclusion;
        }

        // Otherwise fall back to operator settings default
        return operator?.opts?.charter?.marginInclusion || 30;
    }

    /**
     * Calculate quote from itinerary legs including deadrun costs
     * @param {Object} options - Options for quote calculation
     * @param {Object} options.operator - Operator object with payment rates
     * @param {Object} options.allVehicleTypes - All vehicle types
     * @returns {Promise<Array>} Array of quote line items
     */
    async calculateQuoteFromItinerary(options = {}) {
        const { 
            operator, 
            allVehicleTypes = {},
            allSchedules = {},
            allCharterSchedules = {}
        } = options;

        const schedules = {
            ...allSchedules,
            ...allCharterSchedules
        };

        if (!this.itinerary || this.itinerary.length === 0) {
            return [];
        }

        // Calculate total distance and duration for all legs
        let totalDistance = 0;
        let totalDuration = 0;
        let deadrunDistance = 0;
        let deadrunDuration = 0;
        let driverCost = 0;
        let totalVehicleCost = 0;
        let totalCost = 0;
        let driverCostByDate = {};
        let driverCostByDayType = {
            'Weekday': {count: 0, totalCost: 0, details: []},
            'Saturday': {count: 0, totalCost: 0, details: []},
            'Sunday': {count: 0, totalCost: 0, details: []},
            'Public Holiday': {count: 0, totalCost: 0, details: []}
        };

        const getRounded = (value) => {
            return Math.round(value * 100) / 100;
        };
        
        // Store leg details for the quote
        const legDetails = [];
        
        let numberOfVehicles = 0;

        const rates = {
            ...defaultRates,
            ...operator?.opts?.charter?.paymentRates,
        };

        // For standard bookings, calculate costs for each date
        let dates;
        if (this.charterType === 'standard') {
            dates = this.dates?.length ? this.dates : [dayjs()];
        } else if (this.charterType === 'recurring') {
            // For recurring bookings, get all running dates from the schedule
            const schedule = schedules[this.scheduleId];
            if (!schedule) {
                dates = [dayjs()];
            } else {
                dates = schedule.getRunningDates();
            }
        } else {
            dates = [dayjs()];
        }

        // Calculate distance and duration for all legs
        this.itinerary.map((leg, index) => {
            let _deadrunDistance = 0;
            let _deadrunDuration = 0;
            let startDepotToFirstStop = 0;
            let lastStopToEndDepot = 0;

            const firstStop = leg.stops[0];
            const lastStop = last(leg.stops);

            if (leg.startDepot && firstStop) {
                startDepotToFirstStop = leg.startDepotDistance || 0;
                _deadrunDistance += startDepotToFirstStop;
                _deadrunDuration += leg.startDepotDuration || 0;
            }

            if (leg.endDepot && lastStop) {
                lastStopToEndDepot = leg.endDepotDistance || 0;
                _deadrunDistance += lastStopToEndDepot;
                _deadrunDuration += leg.endDepotDuration || 0;
            }

            const legDistance = leg.distance || 0;
            const legDuration = leg.duration || 0;

            // Calculate costs for each trip
            const trips = leg.services;
            let legNumberOfVehicles = 0;
            const tripCosts = [];
            let totalVehicleCostForLeg = 0;
            let hasMissingVehicles = trips.length > 0 ? false : true;

            // Calculate vehicle costs per trip
            trips.forEach(trip => {
                const vehicleBreakdown = [];
                const tripVehicleCost = trip.vehicleTypes?.reduce((acc, vehicleTypeId) => {
                    const vehicle = allVehicleTypes[vehicleTypeId];
                    const quantity = trip.vehicleTypeQuantities?.[vehicleTypeId] || 1;
                    legNumberOfVehicles += quantity;
                    numberOfVehicles += quantity;
                    vehicleBreakdown.push({
                        vehicleTypeId,
                        name: vehicle?.vehicleTypeName,
                        quantity,
                        rate: vehicle?.rate || DEFAULT_VEHICLE_RATE,
                        total: (vehicle?.rate || DEFAULT_VEHICLE_RATE) * quantity
                    });
                    return acc + ((vehicle?.rate || DEFAULT_VEHICLE_RATE) * quantity);
                }, 0) || 0;

                const tripDeadrunCost = tripVehicleCost * getRounded(_deadrunDistance / 1000);
                const tripDistanceCost = tripVehicleCost * getRounded(legDistance / 1000);
                // Only multiply by dates.length for recurring charters
                let tripTotalCost;
                if (this.charterType === 'recurring') {
                    tripTotalCost = (tripDeadrunCost + tripDistanceCost) * dates.length;
                } else {
                    tripTotalCost = (tripDeadrunCost + tripDistanceCost);
                }
                
                totalVehicleCostForLeg += tripTotalCost;
                const hasVehicles = trip.vehicleTypes?.length > 0;

                if(!hasMissingVehicles && !hasVehicles) {
                    hasMissingVehicles = true;
                }
                
                tripCosts.push({
                    tripId: trip.tripId,
                    vehicleBreakdown,
                    vehicleCost: tripVehicleCost,
                    deadrunCost: tripDeadrunCost,
                    distanceCost: tripDistanceCost,
                    totalCost: tripTotalCost,
                    hasVehicles
                });
            });

            totalVehicleCost += totalVehicleCostForLeg;

            // Calculate total distance and duration for all trips
            const singleTripDistance = leg.getDistance();
            const totalLegDistance = singleTripDistance * trips.length;
            const totalLegDeadrun = _deadrunDistance * trips.length;
            
            totalDistance += totalLegDistance;
            deadrunDistance += totalLegDeadrun;

            let totalLegDuration = 0;
            trips.forEach(trip => {
                totalLegDuration += trip.getDuration(leg) + (leg.startDepotDuration || 0) + (leg.endDepotDuration || 0);
            });
            totalDuration += totalLegDuration;

            // Calculate costs for each date
            const legDriverCostByDate = {};
            const legDriverCostByDayType = {
                'Weekday': {count: 0, totalCost: 0, details: []},
                'Saturday': {count: 0, totalCost: 0, details: []},
                'Sunday': {count: 0, totalCost: 0, details: []},
                'Public Holiday': {count: 0, totalCost: 0, details: []}
            };

            let numberOfDates = dates.length;

            // const legDates = this
            dates.forEach(operatingDate => {
                const isHoliday = this.isHoliday(operatingDate);
                const dayOfWeek = operatingDate.day();

                let dayType = 'Weekday';
                if (isHoliday) {
                    dayType = 'Public Holiday';
                } else if (dayOfWeek === 0) {
                    dayType = 'Sunday';
                } else if (dayOfWeek === 6) {
                    dayType = 'Saturday';
                }
                const dateKey = operatingDate.format(DATE_STRING);
                if(!legDriverCostByDayType[dayType].numberOfDates) {
                    legDriverCostByDayType[dayType].numberOfDates = 0;
                }
                legDriverCostByDayType[dayType].numberOfDates++;
                legDriverCostByDayType[dayType].numberOfTrips = trips.length;

                let tripProcessed = false;
                trips.forEach(trip => {
                    // For standard bookings, use trip dates from getDriverRequirements instead of stopTimes[0].date
                    if (this.charterType === 'standard') {
                        const driverRequirements = this.getDriverRequirements();
                        const itinReq = driverRequirements[leg.routeId];
                        const tripReq = itinReq?.trips[trip.tripId];
                        const tripDates = tripReq?.dates || [];
                        // Only process if this operatingDate matches one of the trip's dates
                        if (!tripDates.some(dateStr => operatingDate.isSame(dayjs(dateStr, DATE_STRING), 'day'))) return; // skip if not the trip's date
                        numberOfDates = 1;
                    }
                    // Only create legDriverCostByDate[dateKey] if a trip is processed for this date
                    if (!legDriverCostByDate[dateKey]) {
                        legDriverCostByDate[dateKey] = {
                            date: dateKey,
                            dayType,
                            totalDriverCost: 0,
                            trips: {},
                            numberOfVehicles: legNumberOfVehicles,
                            numberOfDates: 0,
                            numberOfTrips: 0
                        };
                    }
                    tripProcessed = true;
                    const tripSeconds = trip.getDuration(leg);
                    const days = Math.floor(tripSeconds / 86400);
                    const hours = Math.floor((tripSeconds % 86400) / 3600);
                    const minutes = Math.ceil((tripSeconds % 3600) / 60);
                    const totalDurationSeconds = days * 86400 + hours * 3600 + minutes * 60;
                    // const totalDurationHours = formatToHoursAndMinutes(totalDurationSeconds + (_deadrunDuration || 0));
                    const totalDurationHours = Math.round(((totalDurationSeconds + (_deadrunDuration || 0)) / 3600) * 100) / 100;


                    const applicableRates = rates.penaltyRates[dayType];
                    const baseRate = rates.baseRate || 50;
                    const costDetails = calculateDriverCost(totalDurationHours, baseRate, applicableRates);
                    const driverCostPerVehicle = costDetails.driverCostPerVehicle;
                    const tripDriverCost = driverCostPerVehicle * legNumberOfVehicles;

                    // Store trip-specific costs
                    legDriverCostByDate[dateKey].trips[trip.tripId || '_default'] = {
                        tripId: trip.tripId || '_default',
                        driverCost: tripDriverCost,
                        regularHours: costDetails.regularHours,
                        tier1OTHours: costDetails.tier1OTHours,
                        tier2OTHours: costDetails.tier2OTHours,
                        regularPay: costDetails.regularPay,
                        regularPayByNumberOfVehicles: costDetails.regularPay * legNumberOfVehicles,
                        regularPayWithLoading: costDetails.regularPayWithLoading,
                        regularPayWithLoadingByNumberOfVehicles: costDetails.regularPayWithLoading * legNumberOfVehicles,
                        tier1OTPay: costDetails.tier1OTPay,
                        tier1OTPayByNumberOfVehicles: costDetails.tier1OTPay * legNumberOfVehicles,
                        tier1OTPayWithLoading: costDetails.tier1OTPayWithLoading,
                        tier1OTPayWithLoadingByNumberOfVehicles: costDetails.tier1OTPayWithLoading * legNumberOfVehicles,
                        tier2OTPay: costDetails.tier2OTPay,
                        tier2OTPayByNumberOfVehicles: costDetails.tier2OTPay * legNumberOfVehicles,
                        tier2OTPayWithLoading: costDetails.tier2OTPayWithLoading,
                        tier2OTPayWithLoadingByNumberOfVehicles: costDetails.tier2OTPayWithLoading * legNumberOfVehicles,
                        // Add hourly rates
                        regularHourlyRate: costDetails.regularHourlyRate,
                        tier1OTHourlyRate: costDetails.tier1OTHourlyRate,
                        tier2OTHourlyRate: costDetails.tier2OTHourlyRate
                    };

                    // Update total driver cost for this date
                    legDriverCostByDate[dateKey].totalDriverCost += tripDriverCost;

                    // Update day type costs
                    legDriverCostByDayType[dayType].count++;
                    legDriverCostByDayType[dayType].totalCost += tripDriverCost;
                    legDriverCostByDayType[dayType].details.push({
                        tripId: trip.tripId || '_default',
                        routeId: leg.routeId,
                        routeName: leg.routeName,
                        driverCost: tripDriverCost,
                        ...costDetails,
                        regularPayByNumberOfVehicles: costDetails.regularPay * legNumberOfVehicles,
                        regularPayWithLoadingByNumberOfVehicles: costDetails.regularPayWithLoading * legNumberOfVehicles,
                        tier1OTPayByNumberOfVehicles: costDetails.tier1OTPay * legNumberOfVehicles,
                        tier1OTPayWithLoadingByNumberOfVehicles: costDetails.tier1OTPayWithLoading * legNumberOfVehicles,
                        tier2OTPayByNumberOfVehicles: costDetails.tier2OTPay * legNumberOfVehicles,
                        tier2OTPayWithLoadingByNumberOfVehicles: costDetails.tier2OTPayWithLoading * legNumberOfVehicles,
                    });

                    // Update global costs
                    driverCost += tripDriverCost;
                });
                // If no trip was processed for this date (standard bookings), remove the empty entry
                if (this.charterType === 'standard' && !tripProcessed && legDriverCostByDate[dateKey]) {
                    delete legDriverCostByDate[dateKey];
                }
            });
            
            // Store leg details for the quote
            legDetails.push({
                index,
                routeId: leg.routeId,
                routeName: leg.routeName,
                distance: totalLegDistance || 0,
                duration: totalLegDuration || 0,
                deadrunDistance: deadrunDistance || 0,
                deadrunDuration: deadrunDuration || 0,
                startDepotToFirstStop: startDepotToFirstStop || 0,
                lastStopToEndDepot: lastStopToEndDepot || 0,
                tripCosts,
                totalVehicleCost: totalVehicleCostForLeg,
                vehicleCount: legNumberOfVehicles,
                driverCostByDate: this.charterType === 'standard' ? legDriverCostByDate : null,
                driverCostByDayType: this.charterType === 'recurring' ? legDriverCostByDayType : null,
                totalDriverCost: this.charterType === 'standard' ?
                    Object.values(legDriverCostByDate).reduce((total, dateInfo) => total + dateInfo.totalDriverCost, 0) :
                    Object.values(legDriverCostByDayType).reduce((total, typeInfo) => total + typeInfo.totalCost, 0),
                numberOfDates: numberOfDates,
                hasMultipleTrips: trips.length > 1,
                numberOfTrips: trips.length,
                perTripDistance: leg.distance || 0,
                perTripDeadrun: _deadrunDistance || 0,
                perTripDuration: leg.duration || 0,
                perTripDeadrunDuration: _deadrunDuration || 0,
                hasMissingVehicles: hasMissingVehicles
            });
        });

        // Calculate total distance and duration including deadrun
        const grandTotalDistance = totalDistance + deadrunDistance;
        const grandTotalDuration = totalDuration;

        // Always use quantity of 1 since we're calculating total costs properly
        const baseQuantity = 1;

        // Calculate the base unit price (total cost for all dates)
        let baseUnitPrice = totalVehicleCost + driverCost;

        // Get margin inclusion percentage
        const operatorMarginInclusion = this.getMarginInclusion(operator);
        let marginInclusionAmount;
        const discountPercent = this.getDiscount();
        let discountAmount;
        const sellingPrice = operatorMarginInclusion > 0 ? baseUnitPrice / (1 - ((operatorMarginInclusion) / 100)) : baseUnitPrice;

        // Apply margin inclusion to the price
        if (operatorMarginInclusion > 0) {
            marginInclusionAmount = sellingPrice - baseUnitPrice;
        }

        // Apply adjustments
        const adjustedQuantity = baseQuantity + (this.manualAdjustments?.quantity || 0);
        let adjustedUnitPrice = sellingPrice + (this.manualAdjustments?.unitPrice || 0);
        totalCost = adjustedUnitPrice * adjustedQuantity;

        if(discountPercent > 0) {
            discountAmount = adjustedUnitPrice * (discountPercent / 100);
        }

        const lineItemHasMissingVehicles = legDetails.some(leg => leg.hasMissingVehicles);

        const lineItem = {
            name: this.name || 'Charter Service',
            description: `Complete booking with ${this.itinerary.length} charter(s)`,
            unitPrice: adjustedUnitPrice,
            sellingPrice,
            quantity: adjustedQuantity,
            discount: discountAmount * adjustedQuantity,
            margin: marginInclusionAmount * adjustedQuantity,
        }

        // Create quote line item with adjustments applied
        const baseQuote = {
            name: this.name || 'Charter Service',
            description: `Complete booking with ${this.itinerary.length} charter(s)`,
            unitPrice: adjustedUnitPrice,
            sellingPrice,
            quantity: adjustedQuantity,
            discount: discountAmount * adjustedQuantity,
            margin: marginInclusionAmount * adjustedQuantity,
            baseUnitPrice: baseUnitPrice,
            amount: this.calculateLineItemAmount(lineItem),
            total: this.calculateLineItemAmount(lineItem),
            type: 'auto-generated',
            details: {
                totalDistance: totalDistance + deadrunDistance,
                totalDuration,
                deadrunDistance,
                deadrunDuration,
                driverCost,
                driverCostByDate: this.charterType === 'standard' ? driverCostByDate : null,
                driverCostByDayType: this.charterType === 'recurring' ? driverCostByDayType : null,
                rates,
                vehicleCost: totalVehicleCost,
                baseUnitPrice,
                baseQuantity,
                unitPriceAdjustment: this.manualAdjustments?.unitPrice || 0,
                quantityAdjustment: this.manualAdjustments?.quantity || 0,
                marginInclusion: operatorMarginInclusion,
                marginInclusionAmount,
                sellingPrice,
                discount: discountPercent,
                legDetails,
                numberOfVehicles,
                hasMissingVehicles: lineItemHasMissingVehicles,
                numberOfDates: dates.length,
            }
        };

        return [baseQuote];
    }

    /**
     * Generate quote from itinerary legs
     * @param {Object} options - Options for quote generation
     * @param {Object} options.operator - Operator object with payment rates
     * @param {Object} options.allVehicleTypes - All vehicle types
     * @returns {Promise<Charter>} Updated charter with quote line items
     */
    async generateQuote(options = {}, override = false) {
        // Find the auto-generated quote line item if it exists
        const autoGeneratedIndex = this.quoteLineItems.findIndex(item => item.type === 'auto-generated');
        const hasUpdatedAutoGenerated = this.quoteLineItems.some(item => item.type === 'auto-generated' && item.updatedAutogenerated);

        if (hasUpdatedAutoGenerated && !override) {
            return this;
        }
        
        // Calculate the new auto-generated quote
        const autoGeneratedQuote = await this.calculateQuoteFromItinerary(options);
        if (autoGeneratedIndex >= 0) {
            // Update the existing auto-generated quote
            this.quoteLineItems[autoGeneratedIndex] = autoGeneratedQuote[0];
        } else {
            // Add the auto-generated quote at the beginning
            this.quoteLineItems = [...autoGeneratedQuote, ...this.quoteLineItems];
        }

        this.quoteLineItems = this.quoteLineItems.map(item => {
            item.total = item.unitPrice * item.quantity;
            item.amount = item.unitPrice * item.quantity;
            item.baseUnitPrice = item.baseUnitPrice || item.unitPrice;
            return item;
        });

        this.autoGeneratedQuote = true;
        return this;
    }

    markAsSent() {        
        if(this.quoteStatus === QUOTE_STATUS.draft.value) {
            this.quoteStatus = QUOTE_STATUS.sent.value;
        }

        if(this.quoteStatus === QUOTE_STATUS.invoiceDraft.value) {
            this.quoteStatus = QUOTE_STATUS.awaitingPayment.value;
        }
    }

    getRunningDates(schedulesById) {
        if (!this.duties?.length) {
            return 0;
        }
        return this.duties.reduce((duties, duty) => duties.concat(duty.getRunningDates(schedulesById)), []);
    }

    getDuration() {
        if (!this.duties?.length) {
            return 0;
        }
        return this.duties.reduce((acc, duty) => acc + duty.getShiftTime(), 0);
    }

    getDistance() {
        if (!this.duties?.length) {
            return 0;
        }
        return this.duties.reduce((acc, duty) => acc + duty.getShiftDistance(), 0);
    }

    getName() {
        return this.name || this.duty?.getName?.() || this.duty?.shiftBatNumber;
    }

    isValid() {
        return true;
    }

    clone() {
        return new Charter(cloneDeep(this));
    }

    toJson() {
        const json = {
            ...this,
            itinerary: this.itinerary?.map(i => i?.routeId),
            duties: this.duties?.map(duty => duty?.shiftBatId),
            startDate: formatToDateString(this.startDate),
            endDate: formatToDateString(this.endDate),
            dates: this.dates?.map(date => formatToDateString(date)),
            quoteDate: formatToDateString(this.quoteDate),
            paymentDue: formatToDateString(this.paymentDue),
            invoiceDetails: this.invoiceDetails?{
                ...this.invoiceDetails,
                invoiceDate: formatToDateString(this.invoiceDetails?.invoiceDate),
                dueDate: formatToDateString(this.invoiceDetails?.dueDate),
            }: {},
            confirmationDetails: this.confirmationDetails ? {
                ...this.confirmationDetails,
                confirmedDate: formatToDateString(this.confirmationDetails?.confirmedDate),
                depositDueDate: formatToDateString(this.confirmationDetails?.depositDueDate),
            } : {},
            receiptDetails: this.receiptDetails ? {
                ...this.receiptDetails,
                receiptDate: formatToDateString(this.receiptDetails?.receiptDate),
            } : {},
        };
        delete json.vehicle;
        delete json.customer;
        return json;
    }

    fromJson() {
        this.itinerary = this.itinerary?.map(route => typeof route === 'object' ? new BusRoute(route) : route);
    }

    from(data) {
        return new Charter(data);
    }

    /**
     * Update manual adjustments for auto-generated quote
     * @param {Object} adjustments - The adjustments to apply
     * @param {number} adjustments.unitPrice - The unit price adjustment
     * @param {number} adjustments.quantity - The quantity adjustment
     */
    updateQuoteAdjustments(adjustments) {
        this.manualAdjustments = {
            ...this.manualAdjustments,
            ...adjustments
        };
    }

    /**
     * Reset manual adjustments for auto-generated quote
     */
    resetQuoteAdjustments() {
        this.manualAdjustments = {
            unitPrice: 0,
            quantity: 0
        };
    }

    /**
     * Calculate the amount for a single quote line item
     * @param {Object} line - Quote line item
     * @returns {number} - Final amount after discount
     */
    calculateLineItemAmount(line) {
        const discount = line.discount || 0;
        if (line.unitPrice && line.quantity) {
            const total = line.unitPrice * line.quantity - discount;
            return total < 0 ? 0 : total;
        }
        return 0;
    }

    calculateLineItemDiscount(line) {
        const discount = this.getDiscount();
        const sellingPrice = line.sellingPrice || line.unitPrice;
        return sellingPrice * (discount / 100) * line.quantity;
    }

    /**
     * Calculate subtotal of all quote line items
     * @returns {number} - Subtotal before charter-level discount
     */
    calculateSubTotal() {
        return (this.quoteLineItems || []).reduce((acc, current) => {
            return acc + parseFloat(this.calculateLineItemAmount(current));
        }, 0) || 0;
    }

    /**
     * Calculate total after charter-level discount
     * @param {Object} operator - Operator object with settings
     * @returns {number} - Total after discount
     */
    calculateTotalAfterDiscount() {
        return parseFloat(this.calculateSubTotal())  || 0
    }

    /**
     * Calculate GST amount
     * @param {Object} operator - Operator object with settings
     * @returns {number} - GST amount
     */
    calculateGST() {
        return (parseFloat(this.calculateTotalAfterDiscount()) * 0.1) || 0;
    }

    /**
     * Calculate final total including GST
     * @param {Object} operator - Operator object with settings
     * @returns {number} - Final total
     */
    calculateTotal() {
        const totalAfterDiscount = parseFloat(this.calculateTotalAfterDiscount()) || 0;
        const gst = parseFloat(this.calculateGST()) || 0;
        return totalAfterDiscount + gst;
    }

    // Update the document action handlers to include timestamps
    handleDocumentAction(action) {
        const currentDate = dayjs();
        
        switch (action.type) {
            case 'createConfirmation':
                this.confirmationDetails = action.payload.confirmationDetails;
                this.confirmationDate = currentDate.format('DD/MM/YYYY HH:mm');
                break;
            case 'createInvoice':
                this.invoiceDetails = action.payload.invoiceDetails;
                this.invoiceDate = currentDate.format('DD/MM/YYYY HH:mm');
                break;
            case 'createReceipt':
                this.receiptDetails = action.payload.receiptDetails;
                this.receiptDate = currentDate.format('DD/MM/YYYY HH:mm');
                break;
        }
    }

    getValidationsMessages() {
        const validations = [];
        const autoGeneratedLine = this.quoteLineItems.find(line => line.type === 'auto-generated');
        const hasMissingVehicles = autoGeneratedLine?.details?.hasMissingVehicles;
        const margin = this.getMarginInclusion();
        if (hasMissingVehicles) {
            validations.push({
                field: 'autoGeneratedCharters',
                message: 'No vehicles assigned to some charters'
            });
        }
        if (margin === 0) {
            validations.push({
                field: 'autoGeneratedChartersWithMargin',
                message: 'No margin assigned to booking'
            });
        }

        // check route validations
        const routeValidations = this.itinerary.map(route => route.validate());

        if(routeValidations.some(v => !v.isValid)) {
            validations.push({
                field: 'itinerary',
                message: 'Some charter routes have validations errors'
            });
        }

        return validations;
    }
}

export class Itinerary {
    constructor(props) {
        this.charterId = ulid();
        this.type = 'charter#itinerary';

        Object.assign(this, props);

        this.route = new BusRoute(this.route ?? {});
        this.shift = new ShiftBat();
    }

    isValid() {
        return true;
    }

    clone() {
        return new Itinerary(cloneDeep(this));
    }
}

export class ItineraryShift extends ShiftBat {
    constructor(props, modelData = null) {
        super(props);
        this.charterId = ulid();
        this.type = 'charter#itinerary';
        Object.assign(this, props);

        if (this.rows.length) {
            this.rows = this.rows.map((row) => this.createRow(row, modelData));
        }
        if (typeof this.createdAt === 'string') {
            this.createdAt = dayjs(this.createdAt, 'DD/MM/YYYY');
        }
        if (typeof this.updatedAt === 'string') {
            this.updatedAt = dayjs(this.updatedAt, 'DD/MM/YYYY');
        }
    }

    clone(field, value) {
        const newShiftBat = new ItineraryShift({
            ...this,
            rows: this.rows.map((r) => r?.clone()),
        });
        if (field?.length) {
            newShiftBat[field] = value;
        }
        return newShiftBat;
    }

    checkPrevLoc(row, idx) {
        let addedDeadRow = false;
        if (idx > 0 && row.type !== CharterRowType.charter) {
            let prevIdx = idx - 1;
            let prevRow = this.rows[prevIdx--];

            if (prevRow.type === CharterRowType.charter) {
                return false;
            }

            // Add dead running if necessary
            if (row.getStartLocation) {
                const toLoc = row.getStartLocation();
                if (toLoc) {
                    let prevLocRow = prevRow,
                        prevTimeRow = Number.isFinite(prevRow.time) && prevRow.time > -1 ? prevRow : null;
                    while (prevIdx >= 0 && !prevLocRow.getEndLocation) {
                        prevLocRow = this.rows[prevIdx--];
                        if (!prevTimeRow && Number.isFinite(prevLocRow.time) && prevLocRow.time > -1) {
                            prevTimeRow = prevLocRow;
                        }
                    }

                    if (prevLocRow.getEndLocation) {
                        const fromLoc = prevLocRow.getEndLocation();
                        if (fromLoc && toLoc.geohash !== fromLoc.geohash) {
                            const distance = getDistanceInMetres(prevLocRow.getEndLocation(), row.getStartLocation());
                            const duration = distance / 14;

                            const deadRow = this.createRow({
                                type: CharterRowType.charter,
                                time: prevTimeRow ? prevTimeRow.time + (prevTimeRow.duration || 0) : -1,
                                start: prevLocRow.getEndLocation(),
                                startStopId: prevLocRow.getEndLocation().stopId,
                                end: row.getStartLocation(),
                                endStopId: row.getStartLocation().stopId,
                                distance,
                                duration,
                                modelData: this.modelData,
                            });
                            this.rows.splice(idx, 0, deadRow);
                            addedDeadRow = true;
                            prevRow = deadRow;
                        }
                    }
                }
            }

            // Update the new rows time
            // if (row.time === -1 && ![ShiftBatRowType.service].includes(row.type)) {
            //     row.time = prevRow.time + (prevRow.duration || 0)
            // }
            row.prevRow = prevRow;
        }
        return addedDeadRow;
    }

    async updateRows({apiKey, allStops, allTransfers, allRoutes, charterModelData, deadrunModelData, opts = {}}) {
        console.log('Updating rows...');

        // eslint-disable-next-line
        let prevRow, nextRow;
        for (let i = 0; i < this.rows.length; i++) {
            if (i > 0) {
                // eslint-disable-next-line
                prevRow = this.rows[i - 1];
            }
            let row = this.rows[i];
            if (i < this.rows.length - 1) {
                // eslint-disable-next-line
                nextRow = this.rows[i + 1];
            }

            if (i > 0 && [CharterRowType.service, CharterRowType.stop].includes(row.type)) {
                if (this.checkPrevLoc(row, i)) {
                    i++;
                }
            }
        }

        await Promise.all(
            this.rows
                .filter((row) => row.updateRow)
                .map(async (row) =>
                    row.updateRow({
                        apiKey,
                        allStops,
                        allTransfers,
                        allRoutes,
                        charterModelData,
                        deadrunModelData,
                    })
                )
        );

        return this;
    }

    deleteNextDeadRow(idx) {
        for (let i = idx + 1; i < this.rows.length; i++) {
            const row = this.rows[i];
            if ([CharterRowType.service, CharterRowType.stop].includes(row.type)) {
                return;
            }
            if (row.type === CharterRowType.charter) {
                this.rows.splice(i, 1);
                return;
            }
        }
    }

    deletePrevDeadRow(idx) {
        for (let i = idx - 1; i >= 0; i--) {
            const row = this.rows[i];
            if ([CharterRowType.service, CharterRowType.stop].includes(row.type)) {
                return;
            }
            if (row.type === CharterRowType.charter) {
                this.rows.splice(i, 1);
                return;
            }
        }
    }

    replaceRow(row, idx = 0) {
        let replace = false;
        let existingIdx = this.rows.findIndex((r) => row.id === r.id);
        if (existingIdx > -1) {
            replace = true;
            idx = existingIdx;
        }
        // const currentRow = this.rows[idx]
        row = this.putRow(idx, row, replace);
        // Check for dead runs on either side. Delete so they will be rebuilt.
        if (row && [CharterRowType.service, CharterRowType.stop, CharterRowType.noteTimed].includes(row.type)) {
            this.deleteNextDeadRow(idx);
            this.deletePrevDeadRow(idx);
        }
        return row;
    }
}

export class CharterTemplate {
    constructor(props) {
        this.charterId = ulid();
        this.type = 'charter#template';

        Object.assign(this, props);
    }

    isValid() {
        return true;
    }

    clone() {
        return new CharterTemplate(cloneDeep(this));
    }
}

export const CharterRowType = {
    noteTimed: 'Note timed',
    note: 'Note',
    service: 'Service',
    break: 'Break',
    breakMeal: 'Meal break',
    breakBroken: 'Broken shift break',
    location: 'Add location',
    stop: 'Point',
    stopNote: 'Stop Note',
    dead: 'Dead running',
    charter: 'Charter route',
    template: 'Template',
};
export const CharterRowTypeReverseMap = {
    'Note timed': 'noteTimed',
    Note: 'note',
    Service: 'service',
    Break: 'break',
    'Meal break': 'breakMeal',
    'Broken shift break': 'breakBroken',
    'Add location': 'location',
    Point: 'stop',
    'Stop Note': 'stopNote',
    'Dead running': 'dead',
    'Charter route': 'charter',
    Template: 'template',
};

export const CharterRowTypeLabel = {
    service: 'Service',
    stop: 'Point',
    noteTimed: 'Note - Timed',
    note: 'Note - Untimed',
    break: 'Break - Crib',
    breakMeal: 'Break - Meal',
    breakBroken: 'Break - Split Shift',
    location: 'Add location',
    stopNote: 'Stop Note',
    dead: 'Dead running',
    charter: 'Charter route',
    template: 'Template',
};

export const SelectableCharterRowTypes = ['template', 'service', 'stop', 'note', 'break', 'breakMeal', 'breakBroken'];
export const CharterRowNotePriority = {normal: 'Normal', high: 'High'};
export const CharterRowNotePriorityLabel = {normal: 'Normal', high: 'Important'};

// // Standard penalty rates table
// const penaltyRates = {
//     'Weekday': {
//         regularHours: 1.0,     // First 10 hours
//         tier1OT: 1.5,          // 10-12 hours
//         tier2OT: 2.0           // >12 hours
//     },
//     'Saturday': {
//         regularHours: 1.5,
//         tier1OT: 1.5,
//         tier2OT: 2.0
//     },
//     'Sunday': {
//         regularHours: 2.0,
//         tier1OT: 2.0,
//         tier2OT: 2.0
//     },
//     'Public Holiday': {
//         regularHours: 2.5,
//         tier1OT: 2.5,
//         tier2OT: 2.5
//     }
// };

const formatToTwoDecimals = (value) => {
    // Use Math.round to handle floating point precision issues
    return Math.round(value * 100) / 100;
};

const formatToHoursAndMinutes = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}.${minutes}`;
};

/**
 * Calculate driver costs with proper order of operations
 * @param {number} hours - Total duration in hours
 * @param {number} baseRate - Base hourly rate
 * @param {Object} applicableRates - Penalty rates for the day type
 * @param {number} casualLoading - Casual loading percentage (default 0.25 or 25%)
 * @returns {Object} Detailed breakdown of driver costs
 */
export const calculateDriverCost = (hours, baseRate, applicableRates, casualLoading = 0.25) => {
    // Calculate hours in each tier
    const regularHours = Math.min(hours, 10);
    const tier1OTHours = Math.min(Math.max(0, hours - 10), 2); // 10-12 hours (max 2)
    const tier2OTHours = Math.max(0, hours - 12); // >12 hours

    // Calculate final hourly rates for each tier (base × multiplier × casual loading)
    const regularHourlyRate = formatToTwoDecimals(baseRate * applicableRates.regularHours * (1 + casualLoading));
    const tier1OTHourlyRate = formatToTwoDecimals(baseRate * applicableRates.tier1OT * (1 + casualLoading));
    const tier2OTHourlyRate = formatToTwoDecimals(baseRate * applicableRates.tier2OT * (1 + casualLoading));

    // Calculate pay for each tier (hourly rate × hours) with precise rounding
    const regularPay = formatToTwoDecimals(regularHourlyRate * regularHours);
    const tier1OTPay = formatToTwoDecimals(tier1OTHourlyRate * tier1OTHours);
    const tier2OTPay = formatToTwoDecimals(tier2OTHourlyRate * tier2OTHours);

    // Calculate total pay with exact rounding at the end
    const driverCostPerVehicle = formatToTwoDecimals(regularPay + tier1OTPay + tier2OTPay);

    return {
        regularHours: formatToTwoDecimals(regularHours),
        tier1OTHours: formatToTwoDecimals(tier1OTHours),
        tier2OTHours: formatToTwoDecimals(tier2OTHours),
        regularPay,
        regularPayWithLoading: regularPay, // Now the same as regularPay since loading is included in rate
        tier1OTPay,
        tier1OTPayWithLoading: tier1OTPay, // Now the same as tier1OTPay since loading is included in rate
        tier2OTPay,
        tier2OTPayWithLoading: tier2OTPay, // Now the same as tier2OTPay since loading is included in rate
        driverCostPerVehicle,
        // Add hourly rates for display purposes
        regularHourlyRate,
        tier1OTHourlyRate,
        tier2OTHourlyRate
    };
};
